import {
  FilterDocumentDateFilter,
  FilterDocumentDurationFilter,
  FilterDocumentFilter,
  FilterDocumentFilters,
  FilterOptionKeys,
  Goal,
  goalOtherID,
  LifeArea,
  lifeAreaOtherID,
  Tag,
} from '@griply/types';
import isEqual from 'lodash/isEqual';

import { isNumber } from './is-number';

export type CountActiveFiltersOptions = {
  defaultFilters?: FilterDocumentFilters | null;
  supportedFilters?: readonly FilterOptionKeys[];
  goals?: Goal[];
  lifeAreas?: LifeArea[];
  tags?: Tag[];
};

type FilterValue =
  | boolean
  | FilterDocumentDurationFilter
  | FilterDocumentDateFilter
  | FilterDocumentFilter<string>;

const DATE_FILTER_KEYS: FilterOptionKeys[] = [
  FilterOptionKeys.Date,
  FilterOptionKeys.Deadline,
  FilterOptionKeys.GoalActive,
  FilterOptionKeys.CompletedAt,
];

const getValidIncludeIds = (
  key: FilterOptionKeys,
  include: string[] | null | undefined,
  goals: Goal[] | undefined,
  lifeAreas: LifeArea[] | undefined,
  tags: Tag[] | undefined,
): string[] => {
  if (!include?.length) {
    return [];
  }
  if (key === FilterOptionKeys.Tag && tags) {
    return include.filter((id) => tags.some((tag) => tag.id === id));
  }
  if (key === FilterOptionKeys.LifeArea && lifeAreas) {
    return include.filter(
      (id) =>
        id === lifeAreaOtherID ||
        lifeAreas.some((lifeArea) => lifeArea.id === id),
    );
  }
  if (key === FilterOptionKeys.Goal && goals) {
    return include.filter(
      (id) => id === goalOtherID || goals.some((goal) => goal.id === id),
    );
  }
  return [...include];
};

const isFilterActive = (
  key: FilterOptionKeys,
  value: FilterValue,
  { goals, lifeAreas, tags }: CountActiveFiltersOptions,
): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (DATE_FILTER_KEYS.includes(key)) {
    return !!(value as FilterDocumentDateFilter).type;
  }
  if (key === FilterOptionKeys.Duration) {
    const duration = value as FilterDocumentDurationFilter;
    return isNumber(duration.more?.value) || isNumber(duration.less?.value);
  }
  const includeValue = value as FilterDocumentFilter<string>;
  const validIds = getValidIncludeIds(
    key,
    includeValue.include,
    goals,
    lifeAreas,
    tags,
  );
  if (validIds.length) {
    return true;
  }
  if (key === FilterOptionKeys.Tag) {
    return !!includeValue.noTag;
  }
  return false;
};

const isFilterDefault = (
  key: FilterOptionKeys,
  value: unknown,
  defaultValue: unknown,
): boolean => {
  if (key === FilterOptionKeys.Goal) {
    const v = (value ?? {}) as { include?: unknown };
    const d = (defaultValue ?? {}) as { include?: unknown };
    return isEqual({ include: v.include }, { include: d.include });
  }
  return isEqual(value, defaultValue);
};

export const countActiveFilters = (
  filters: FilterDocumentFilters | null | undefined,
  options: CountActiveFiltersOptions = {},
): number => {
  if (!filters) {
    return 0;
  }

  const { defaultFilters, supportedFilters } = options;
  const entries = Object.entries(filters) as [
    FilterOptionKeys,
    FilterValue | null | undefined,
  ][];

  return entries.reduce((count, [key, value]) => {
    if (supportedFilters && !supportedFilters.includes(key)) {
      return count;
    }
    if (!value) {
      return count;
    }
    if (!isFilterActive(key, value, options)) {
      return count;
    }
    if (defaultFilters && isFilterDefault(key, value, defaultFilters[key])) {
      return count;
    }
    return count + 1;
  }, 0);
};

export const hasActiveFilters = (
  filters: FilterDocumentFilters | null | undefined,
  options: CountActiveFiltersOptions = {},
): boolean => countActiveFilters(filters, options) > 0;
